"use client";

import Link from "next/link";
import { useEffect, type ComponentProps, type MouseEvent } from "react";

import { ANCORA_SCHEDE } from "@/lib/editoriali-raccolta";

/** Il cambio di pagina porta il lettore all'inizio della sezione delle
 *  schede, ma il ritorno da un articolo deve riportarlo dov'era.
 *
 *  Con un'ancora nell'indirizzo (`?page=2#prospettive`) la prima cosa
 *  funziona e la seconda no: tornando indietro, Next rivede l'ancora e
 *  riporta la pagina alla sezione, sopra la quota che il browser aveva
 *  salvato. Misurato (/tmp/pwmeas/ed-ritorno.js): con l'ancora il ritorno
 *  cade 149 pixel sopra a 1440 e 749 sopra a 390, senza torna esatto.
 *
 *  Quindi niente ancora nei link: un clic su un comando di pagina alza
 *  un segnale, e le schede nuove, appena montate, portano in vista la
 *  sezione. Il ritorno non passa da un clic e resta al browser. Il segnale
 *  vive nel modulo, non in uno stato: deve sopravvivere al cambio di
 *  albero dentro il `<Suspense>`. */
let daScorrere = false;

/** Un link di paginazione: come `<Link>`, senza scorrimento di Next. Un
 *  clic con un tasto modificatore apre una scheda nuova e non alza il
 *  segnale. */
export function LinkPagina(props: Omit<ComponentProps<typeof Link>, "scroll">) {
  const { onClick, ...resto } = props;
  return (
    <Link
      {...resto}
      scroll={false}
      onClick={(e: MouseEvent<HTMLAnchorElement>) => {
        onClick?.(e);
        if (!(e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0)) daScorrere = true;
      }}
    />
  );
}

/** Lo stesso segnale alzato dalla ricerca, che da quando sta in testata
 *  si invia lontano dalle schede: senza, i risultati arriverebbero sotto
 *  l'editoriale della settimana, fuori dalla finestra, e sembrerebbe che
 *  la ricerca non abbia fatto niente. */
export function scorriAllaSezione() {
  daScorrere = true;
}

/** Messo in fondo alle schede: al montaggio, se il segnale è alzato, porta
 *  la sezione in cima alla finestra (con lo `scroll-mt` della sezione).
 *  `ancora` è l'id della sezione: di suo quella degli editoriali; lo
 *  stesso meccanismo serve l'archivio di /report. */
export function AllaSezione({ ancora = ANCORA_SCHEDE }: { ancora?: string } = {}) {
  useEffect(() => {
    if (!daScorrere) return;
    daScorrere = false;
    document.getElementById(ancora)?.scrollIntoView({ block: "start" });
  }, [ancora]);
  return null;
}
